import React, { useState, useEffect } from 'react';
import { getObjectives } from '@/services/objectivesService';
import { getPlans } from '@/services/plansService';
import { Objective } from '@/types/objective';
import { Plan } from '@/types/plan';
import { useObjectiveSelector } from '../pricing/hooks/useObjectiveSelector';
import { ObjectiveSection } from '../pricing/ObjectiveSection';
import PlanFeatures from '../pricing/PlanFeatures';
import FAQs from '../pricing/FAQs';

interface IrreplacablePricingProps {
  typeformUrl: string | null;
}

export default function IrreplacablePricing({ typeformUrl }: IrreplacablePricingProps) {
  const [selectedObjective, setSelectedObjective] = useState('');
  const [selectedObjectiveData, setSelectedObjectiveData] = useState<Objective | null>(null);
  const [objectives, setObjectives] = useState<Objective[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  
  const {
    isDropdownOpen,
    needsAttention,
    dropdownRef,
    toggleDropdown,
  } = useObjectiveSelector();

  useEffect(() => {
    async function loadData() {
      try {
        const [objectivesData, plansData] = await Promise.all([
          getObjectives(),
          getPlans()
        ]);
        setObjectives(objectivesData);
        setPlans(plansData);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    }
    loadData();
  }, []);

  useEffect(() => {
    const objective = objectives.find(obj => obj.name === selectedObjective);
    setSelectedObjectiveData(objective || null);
  }, [selectedObjective, objectives]);

  const handleGetStarted = async (planId: string) => {
    if (!typeformUrl) {
      console.error('Typeform URL not available');
      return;
    }

    try {
      // Construct the URL with the plan ID
      const url = new URL(typeformUrl);
      url.searchParams.set('planid', planId);
      
      // Open the typeform in a new tab
      window.open(url.toString(), '_blank');
    } catch (error) {
      console.error('Error opening typeform:', error);
    }
  };

  return (
    <section id="pricing" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ObjectiveSection
          objectives={objectives}
          selectedObjective={selectedObjective}
          onSelect={setSelectedObjective}
          isOpen={isDropdownOpen}
          onToggle={toggleDropdown}
          needsAttention={needsAttention}
          selectedObjectiveData={selectedObjectiveData}
          dropdownRef={dropdownRef}
        />

        <h3 className="text-2xl font-bold text-gray-900 text-center mb-12">
          Then, choose the plan that best fits your growth journey
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`${
                plan.isAdvanced 
                  ? 'relative before:absolute before:inset-[-2px] before:rounded-2xl before:bg-gradient-to-r before:from-[#f9c55f] before:via-[#fce0aa] before:to-[#f9c55f] before:animate-gradient' 
                  : 'bg-white rounded-2xl p-8 border-2 border-gray-200'
              } flex flex-col h-full ${
                plan.isAdvanced ? 'sm:col-span-2 lg:col-span-1' : ''
              }`}
            >
              
              <div className={`${
                plan.isAdvanced 
                  ? 'bg-white rounded-2xl p-8 relative z-20' 
                  : ''
              } flex flex-col h-full`}>
                <div className="flex flex-col">
                  <div className="text-center mb-4 min-h-[4rem] flex flex-col justify-center">
                    <h3 className="text-2xl font-bold text-gray-900">{plan.name}</h3>
                  </div>
                  <p className="text-center text-gray-600 mb-6">{plan.description}</p>
                  <div className="text-center mb-8">
                    <span className="text-5xl font-bold text-[#151b59] line-through decoration-red-500">${plan.basePrice}</span>
                    <span className="text-5xl font-bold text-[#151b59] ml-2">$0</span>
                    <div className="text-gray-500 mt-2">Limited time offer</div>
                    <button
                      onClick={() => handleGetStarted(plan.id)}
                      className="w-full bg-[#ee99ea] text-dark rounded-lg px-6 py-3 hover:bg-[#f0a5ec] transition-colors mt-6"
                    >
                      Start My Free Journey
                    </button>
                  </div>
                  <PlanFeatures 
                    mainFeatures={plan.mainFeatures} 
                    features={plan.features} 
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <FAQs />
      </div>
    </section>
  );
}